import React from 'react';
import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';
import {
  minBreakpointQuery,
  sectionPaddings,
  standardColours,
  headingStyles,
  brandColours,
  fontSize,
  fontWeights,
} from '../styles';
import { Container, HtmlContent, Button, Svg, DateRange, Link } from './ui';
import PriceCard from './PriceCard';
import locationPin from '../images/location-pin.inline.svg';

const StyledSubBanner = styled.section`
  position: relative;
  ${sectionPaddings('30px', '70px')};
  background-color: ${({ backgroundColour }) =>
    backgroundColour ? backgroundColour.hex : brandColours.septenary};
`;

const StyledBackgroundImage = styled(GatsbyImage)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  ${({ backgroundColour }) => {
    if (backgroundColour) {
      return css`
        opacity: 0.2;
      `;
    }
  }};
`;

const StyledInner = styled.div`
  display: grid;
  gap: 30px;

  ${minBreakpointQuery.small`
    gap: 40px;
  `}

  ${({ hasPriceCards }) => {
    if (hasPriceCards) {
      return css`
        ${minBreakpointQuery.large`
          grid-template-columns: repeat(2, 1fr);
          justify-content: space-between;
          align-items: center;
        `}
      `;
    } else {
      return css`
        ${minBreakpointQuery.smedium`
          grid-template-columns: repeat(2, 1fr);
          justify-content: space-between;
          align-items: center;
        `}

        ${minBreakpointQuery.medium`
          gap: 60px;
        `}

        ${minBreakpointQuery.large`
          gap: 80px;
        `}

        ${minBreakpointQuery.xxlarge`
          gap: 100px;
        `}
      `;
    }
  }}
`;

const StyledContent = styled.div`
  position: relative;
  padding: 30px;
  background-color: ${standardColours.white};

  ${minBreakpointQuery.small`
    padding: 35px 40px;
  `}

  ${minBreakpointQuery.smedium`
    max-width: 500px;
  `}

  ${minBreakpointQuery.large`
    padding: 40px 50px;
  `}

  ${minBreakpointQuery.xxlarge`
    padding: 45px 60px;
  `}
`;

const StyledDate = styled(DateRange)`
  margin-bottom: 10px;
`;

const StyledHeading = styled.h1`
  ${headingStyles()};
`;

const StyledSubheading = styled.h2`
  margin-top: 8px;
  color: ${brandColours.primary};
  font-weight: ${fontWeights.semibold};
  ${fontSize(18)};

  ${minBreakpointQuery.small`
    margin-top: 10px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 12px;
    ${fontSize(20)};
  `}
`;

const StyledText = styled(HtmlContent)`
  margin-top: 16px;

  ${minBreakpointQuery.small`
    margin-top: 20px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 24px;
  `}
`;

const StyledLocationWrapper = styled(Link)`
  display: flex;
  gap: 10px;
  align-items: center;
  margin-top: 5px;
`;

const StyledSvg = styled(Svg)`
  flex-shrink: 0;
`;

const StyledLocation = styled.span`
  font-style: normal;
  ${fontSize(12)};

  ${minBreakpointQuery.small`
    ${fontSize(14)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(16)};
  `}
`;

const StyledButton = styled(Button)`
  margin-top: 16px;

  ${minBreakpointQuery.small`
    margin-top: 20px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 24px;
  `}
`;

const StyledPriceCards = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 15px;
`;

const StyledImage = styled(GatsbyImage)`
  margin: auto;
  max-width: 300px;

  ${minBreakpointQuery.small`
    max-width: 350px;
  `}

  ${minBreakpointQuery.large`
    max-width: 400px;
  `}
`;

const SubBanner = ({
  heading,
  subheading,
  text,
  link,
  priceCards,
  image,
  backgroundImage,
  backgroundColour,
  dates,
  venue,
}) => {
  const hasPriceCards = priceCards && priceCards.length > 0;

  return (
    <StyledSubBanner backgroundColour={backgroundColour}>
      {backgroundImage && (
        <StyledBackgroundImage
          image={backgroundImage.gatsbyImageData}
          alt={backgroundImage.alt}
          loading="eager"
          backgroundColour={backgroundColour}
        />
      )}
      <Container>
        <StyledInner hasPriceCards={hasPriceCards}>
          <StyledContent>
            {dates && <StyledDate {...dates} />}
            <StyledHeading>{heading}</StyledHeading>
            {subheading && <StyledSubheading>{subheading}</StyledSubheading>}
            <StyledText content={text} />
            {venue && (
              <StyledLocationWrapper to={venue}>
                <StyledSvg image={locationPin} />
                <StyledLocation>{venue.name}</StyledLocation>
              </StyledLocationWrapper>
            )}
            {link && <StyledButton to={link.pageUrl}>{link.text}</StyledButton>}
          </StyledContent>
          {hasPriceCards && (
            <StyledPriceCards>
              {priceCards.map((priceCard, id) => (
                <PriceCard key={id} {...priceCard} isBanner={true} />
              ))}
            </StyledPriceCards>
          )}
          {image && (
            <StyledImage
              image={image.gatsbyImageData}
              alt={image.alt}
              loading="eager"
            />
          )}
        </StyledInner>
      </Container>
    </StyledSubBanner>
  );
};

export default SubBanner;

export const SubBannerFragment = graphql`
  fragment SubBannerImageFragment on DatoCmsFileField {
    gatsbyImageData(width: 400, imgixParams: { w: "400" })
    alt
  }

  fragment SubBannerBackgroundImageFragment on DatoCmsFileField {
    gatsbyImageData(
      width: 2000
      height: 420
      layout: FULL_WIDTH
      imgixParams: { w: "2000", h: "420" }
    )
    alt
  }
`;
